import { useState } from 'react'
import LayersRoundedIcon from '@mui/icons-material/LayersRounded'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import { BottomSheet } from '../Common/BottomSheet'
import { Checkbox, RailChipTabs, type RailChipTabItem } from '../Common'
import { Pressable } from '../Common/Pressable'
import type {
  CalendarBoardFilterState,
  CalendarStyleId,
  TimelineBoardKey,
} from '../../types/schema'
import { TIMELINE_BOARD_KEYS } from '../../types/schema'
import type { TimelineElement } from '../../context/TimelineContext'
import { CALENDAR_STYLES } from './calendarThemes'
import { CalendarFilterTree } from './CalendarFilterTree'

type OptionsTab = 'boards' | 'style' | 'filter'

interface CalendarOptionsSheetProps {
  isOpen: boolean
  onClose: () => void
  styleId: CalendarStyleId
  onStyleChange: (next: CalendarStyleId) => void
  activeBoard: TimelineBoardKey
  boardScope: { mode: 'current' | 'custom'; boardKeys?: TimelineBoardKey[] }
  onBoardScopeChange: (next: { mode: 'current' | 'custom'; boardKeys?: TimelineBoardKey[] }) => void
  elements: TimelineElement[]
  filterState: CalendarBoardFilterState
  onFilterChange: (next: CalendarBoardFilterState) => void
  initialTab?: OptionsTab
  eduSessionsMode?: 'MINE' | 'ALL' | 'OFF'
  onEduSessionsModeChange?: (mode: 'MINE' | 'ALL' | 'OFF') => void
  canViewAllClasses?: boolean
  showEduClassesControl?: boolean
}

const BOARD_LABEL: Record<TimelineBoardKey, string> = {
  DASHBOARD: 'Dashboard',
  VISIONBOARD: 'Visionboard',
  ARCHIVE: 'Archive',
  BIN: 'Bin',
}

const TAB_CONFIG: Array<{ id: OptionsTab; label: string; Icon: typeof LayersRoundedIcon }> = [
  { id: 'boards', label: 'Boards', Icon: LayersRoundedIcon },
  { id: 'style', label: 'Style', Icon: PaletteRoundedIcon },
  { id: 'filter', label: 'Filter', Icon: TuneRoundedIcon },
]

const TAB_ITEMS: RailChipTabItem<OptionsTab>[] = TAB_CONFIG.map(({ id, label, Icon }) => ({
  key: id,
  label,
  icon: <Icon fontSize="small" />,
}))

export const CalendarOptionsSheet = ({
  isOpen,
  onClose,
  styleId,
  onStyleChange,
  activeBoard,
  boardScope,
  onBoardScopeChange,
  elements,
  filterState,
  onFilterChange,
  initialTab = 'filter',
  eduSessionsMode,
  onEduSessionsModeChange,
  canViewAllClasses,
  showEduClassesControl,
}: CalendarOptionsSheetProps) => {
  const [tab, setTab] = useState<OptionsTab>(initialTab)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) setTab(initialTab)
  }

  const additionalBoards = (boardScope.boardKeys ?? []).filter((key) => key !== activeBoard)

  const setBoardChecked = (board: TimelineBoardKey, checked: boolean) => {
    if (board === activeBoard) return
    const baseSet = new Set([activeBoard, ...additionalBoards])
    if (checked) baseSet.add(board)
    else baseSet.delete(board)
    const nextKeys = Array.from(baseSet)
    onBoardScopeChange({
      mode: nextKeys.length > 1 ? 'custom' : 'current',
      boardKeys: nextKeys,
    })
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Calendar">
      <RailChipTabs<OptionsTab>
        ariaLabel="Options"
        orientation="horizontal"
        value={tab}
        onChange={(key) => setTab(key as OptionsTab)}
        items={TAB_ITEMS}
      />

      <div className="calOptionsSheetBody">
        {tab === 'boards' && (
          <section className="calOptionsSection" aria-labelledby="calOptionsBoardsHead">
            <h3 id="calOptionsBoardsHead" className="calOptionsSectionTitle">Boards in view</h3>
            <p className="calOptionsSectionHint">
              Overlay events from other boards on top of the current one.
            </p>
            <ul className="calOptionsBoardList">
              {TIMELINE_BOARD_KEYS.map((board) => {
                const isCurrent = board === activeBoard
                const isChecked = isCurrent || additionalBoards.includes(board)
                return (
                  <li key={board}>
                    <Checkbox
                      checked={isChecked}
                      disabled={isCurrent}
                      onChange={(next) => setBoardChecked(board, next)}
                      wrapperClassName="calOptionsBoardRow"
                      label={(
                        <>
                          <span className="calOptionsBoardName">{BOARD_LABEL[board]}</span>
                          {isCurrent && <span className="calOptionsBoardTag">current</span>}
                        </>
                      )}
                    />
                  </li>
                )
              })}
            </ul>
          </section>
        )}

          {tab === 'style' && (
            <section className="calOptionsSection" aria-labelledby="calOptionsStyleHead">
              <h3 id="calOptionsStyleHead" className="calOptionsSectionTitle">Calendar style</h3>
              <p className="calOptionsSectionHint">Applies to every view; colours follow your theme.</p>
              <div className="calOptionsStyleGrid">
                {CALENDAR_STYLES.map((style) => (
                  <Pressable
                    key={style.id}
                    className={`calOptionsStyleCard${style.id === styleId ? ' isActive' : ''}`}
                    aria-pressed={style.id === styleId}
                    onClick={() => onStyleChange(style.id)}
                  >
                    <span
                      className="calOptionsStyleSwatch"
                      style={{ background: style.swatch }}
                      aria-hidden="true"
                    />
                    <span className="calOptionsStyleLabel">{style.label}</span>
                  </Pressable>
                ))}
              </div>
            </section>
          )}

          {tab === 'filter' && (
            <section className="calOptionsSection calOptionsSection--tree" aria-label="Filter items">
              <CalendarFilterTree
                elements={elements}
                filterState={filterState}
                onChange={onFilterChange}
                eduSessionsMode={eduSessionsMode}
                onEduSessionsModeChange={onEduSessionsModeChange}
                canViewAllClasses={canViewAllClasses}
                showEduClassesControl={showEduClassesControl}
              />
            </section>
          )}
        </div>
      </BottomSheet>
    )
  }
